/**
 * Icone dell'applicazione (docs/TODO.md, ADR-0008).
 *
 * ## Perché un registro chiuso e non `<FontAwesomeIcon icon={faX}>` ovunque
 *
 * Tutte le icone del progetto passano da qui e sono indirizzate per **nome
 * nostro** (`edit`, `delete`, `wishlist`), non per il nome FontAwesome. Tre
 * ragioni, in ordine di peso:
 *
 *  1. **Un nome sbagliato è un errore di compilazione.** `IconName` è un'unione
 *     chiusa derivata dal registro: con le classi CSS di FontAwesome
 *     (`fa-solid fa-pencl`) un refuso non rende nulla e nessuno se ne accorge.
 *  2. **La stessa azione ha la stessa icona in tutta l'applicazione.** Il
 *     registro è l'unico posto in cui si decide che "rimuovi" è un cestino: due
 *     pagine non possono divergere senza modificarlo.
 *  3. **La dipendenza resta sostituibile.** Nessun file di feature importa
 *     `@fortawesome/*`: cambiare libreria è riscrivere questo file.
 *
 * Il bundle contiene **solo le icone importate qui** — è il motivo per cui
 * l'ADR sceglie i pacchetti SVG e non il webfont CSS, che si porta dietro tutte
 * le ~2000 icone della collezione a prescindere dall'uso.
 *
 * ## `autoAddCss = false`
 *
 * Di suo il core FontAwesome inietta un `<style>` nel `<head>` al primo render.
 * Con una CSP e con il foglio di stile importato staticamente qui sotto è
 * inutile e non deterministico, quindi è disattivato — è la configurazione che
 * la libreria stessa documenta per chi importa il CSS a mano.
 */

import { config, type IconDefinition } from '@fortawesome/fontawesome-svg-core';
import '@fortawesome/fontawesome-svg-core/styles.css';
import {
  faArrowRightArrowLeft,
  faBell,
  faBolt,
  faChartLine,
  faCheck,
  faCircleHalfStroke,
  faCircleInfo,
  faClockRotateLeft,
  faDoorClosed,
  faDoorOpen,
  faEye,
  faMoon,
  faPen,
  faPlus,
  faRobot,
  faRotate,
  faStar,
  faSun,
  faTrashCan,
  faTriangleExclamation,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { ButtonHTMLAttributes } from 'react';
import './icon.css';

config.autoAddCss = false;

/**
 * Registro nome-di-dominio → icona. Le chiavi descrivono **l'azione o il
 * concetto**, non il disegno: se un domani "rimuovi" diventasse una X, qui
 * cambierebbe il valore e non le venti chiamate.
 */
const ICONS = {
  add: faPlus,
  edit: faPen,
  delete: faTrashCan,
  close: faXmark,
  confirm: faCheck,
  history: faClockRotateLeft,
  chart: faChartLine,
  ai: faRobot,
  wishlist: faStar,
  watch: faEye,
  refresh: faRotate,
  notification: faBell,
  info: faCircleInfo,
  warning: faTriangleExclamation,
  trigger: faBolt,
  transfer: faArrowRightArrowLeft,
  marketOpen: faDoorOpen,
  marketClosed: faDoorClosed,
  afterHours: faMoon,
  // Selettore del tema (F-070). `themeDark` condivide il disegno con
  // `afterHours` di proposito: il registro indirizza per concetto, e i due
  // concetti restano liberi di divergere senza toccare chi li usa.
  themeLight: faSun,
  themeDark: faMoon,
  themeAuto: faCircleHalfStroke,
} satisfies Record<string, IconDefinition>;

export type IconName = keyof typeof ICONS;

type IconProps = {
  name: IconName;
  /**
   * Etichetta per chi non vede l'icona. Omessa = icona **decorativa**
   * (`aria-hidden`), che è corretto solo quando accanto c'è già del testo che
   * dice la stessa cosa.
   */
  label?: string;
  className?: string;
  spin?: boolean;
};

export function Icon({ name, label, className, spin = false }: IconProps) {
  return (
    <FontAwesomeIcon
      icon={ICONS[name]}
      className={className ? `icon ${className}` : 'icon'}
      spin={spin}
      // `title` genererebbe un `<title>` dentro l'SVG: `role="img"` +
      // `aria-label` è la forma che i lettori di schermo annunciano in modo
      // uniforme, e il tooltip lo mette chi la usa sul contenitore.
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
    />
  );
}

// ------------------------------------------------------------- IconButton

type IconButtonProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'title'> & {
  icon: IconName;
  /**
   * **Obbligatoria.** Un pulsante di sola icona senza nome accessibile è un
   * pulsante muto: chi usa un lettore di schermo sente "pulsante" e basta.
   * Diventa sia `aria-label` sia `title`, quindi resta anche il suggerimento al
   * passaggio del mouse — che è ciò che rende scopribile un'icona per tutti
   * gli altri.
   */
  label: string;
  variant?: 'ghost' | 'danger';
  loading?: boolean;
};

export function IconButton({
  icon,
  label,
  variant = 'ghost',
  loading = false,
  disabled,
  className = '',
  ...rest
}: IconButtonProps) {
  const classes = ['icon-btn', `icon-btn--${variant}`, className].filter(Boolean).join(' ');

  return (
    <button
      type="button"
      className={classes}
      // Il titolo NON sparisce durante il caricamento: un pulsante che perde il
      // proprio nome mentre lavora è irriconoscibile proprio nel momento in cui
      // ci si chiede che cosa stia facendo.
      title={label}
      aria-label={label}
      disabled={disabled ?? loading}
      {...rest}
    >
      <Icon name={loading ? 'refresh' : icon} spin={loading} />
    </button>
  );
}
